import { Card } from "../components/Card";
import { Cta } from "../components/Cta";
import { Section } from "../components/Section";
import { useAuth } from "../features/auth/context";
import { selectFullList, selectLanding } from "../features/fixture/landing";
import { useFixturesPublic } from "../features/fixture/useFixturesPublic";
import { usePageMeta } from "../lib/usePageMeta";
import { FixtureListSection, FixtureListSkeleton } from "./FixtureListSection";
import styles from "./Home.module.css";

/**
 * The landing spot — every upcoming fixture, every sport, no account needed.
 *
 * ⚠️ REBUILT 2026-09-07 against kev-docs/kev-landing-and-seo-plan.md, the
 * NEWEST of the two navigation docs and the one that governs — it revises
 * kev-football-navigation-proposal.md's "fixtures are the landing spot; free
 * account, Kev's own fair price and fair odds on every row" into a THREE-tier
 * product with an anonymous floor underneath that: "A visitor with no account
 * sees whether Kev has established value in the match — never the winning
 * chances, never the deep dives." So this page no longer reads
 * `useFreeFixtures` (Kev's own numbers, account-tier) — it reads
 * `useFixturesPublic` (`GET /teaser?artifact=fixtures-public`), which has NO
 * NUMBER anywhere in it, and shows a VALUE INDICATOR per row instead of a
 * price. See `FixtureListSection`'s own `ValueChip` note on why its three
 * states may never collapse into two.
 *
 * PUBLIC ROUTE, NOT JUST PUBLIC DATA. `/app` carries no `RequireAuth` any
 * more (see App.tsx's own note) — a stranger and a search crawler both have
 * to reach this page, which was not true of the account-tier list this
 * replaced.
 *
 * ── ORDERED BY WHICHEVER SPORT STARTS NEXT, WINDOWED TO STAY POPULATED ────
 * Ned, 2026-09-07 (verbal, same day as the doc above): group by sport, order
 * the sports by whichever starts soonest — a sport with a game IN PROGRESS
 * ranks first, ahead of one merely next — and show the next 10 fixtures, or
 * everything inside 48 hours if that is more than 10. Both rules, and every
 * edge case they carry (a sport with nothing upcoming, a fixture that has
 * kicked off but not finished), live as PURE FUNCTIONS in
 * `features/fixture/landing.ts` — `selectLanding` for the windowing here,
 * `orderSportsByNextStart` (called inside `FixtureListSection`) for the
 * grouping. Football has the only export pipeline today; NBA, NFL and AFL
 * will land rows in the same file later, keyed by their own `sport` field —
 * neither function hard-codes which sport goes first, so a second sport
 * appearing changes nothing here.
 *
 * The whole point, in Ned's own words: "the site should always look
 * populated." A sport with no eligible fixture is simply absent, never an
 * empty heading, and the card only shows its "nothing on right now" state
 * when there is truly nothing at all — see the `isSuccess` branch below.
 *
 * ── SEE MORE ─────────────────────────────────────────────────────────────
 * `/fixtures` (`AllFixtures.tsx`) is the same list, `selectFullList`'s
 * bigger window — about 20, or everything inside 48 hours if that is more.
 * The link below only appears when that list actually holds more than this
 * one does; there is nothing to see more of otherwise.
 */
export function Home() {
  usePageMeta("home");
  const { status } = useAuth();
  const fixtures = useFixturesPublic();

  const now = new Date();
  const allFixtures = fixtures.data?.fixtures ?? [];
  const landing = selectLanding(allFixtures, now);
  /* Cheap to compute unconditionally — both are pure and the whole list is
     rarely more than a few dozen rows — and comparing lengths of the two
     rules is a more honest test than re-deriving "more exists" separately. */
  const hasMore = selectFullList(allFixtures, now).length > landing.length;

  return (
    <Section eyebrow="Fixtures" heading="What Kev's calling" tone="ink" level={1}>
      <p className={styles.standfirst}>
        Kev&rsquo;s read on every upcoming match. Tap one for the breakdown.
      </p>

      {/* Honest, not pushy: shown to anyone who is not signed in — including
          the boot window, the same "resolves to signed-out" rule
          `destinations.ts` already applies — and gone the instant a session
          is confirmed, never left up for someone it no longer applies to. */}
      {status !== "authenticated" && (
        <Card className={styles.signIn}>
          <p className={styles.signInText}>
            Winning chances and fair odds are free with an account.
          </p>
          <div className={styles.signInActions}>
            <Cta to="/signup">Create free account</Cta>
            <Cta to="/login" variant="secondary">
              Sign in
            </Cta>
          </div>
        </Card>
      )}

      {fixtures.isPending && <FixtureListSkeleton />}

      {fixtures.isError && (
        <p className={styles.muted}>The fixture list is unavailable right now.</p>
      )}

      {fixtures.isSuccess && landing.length === 0 && (
        <Card className={styles.card}>
          <p className={styles.muted}>
            <strong>Nothing on right now.</strong> The card fills as Kev&rsquo;s calls are
            frozen before kick-off.
          </p>
        </Card>
      )}

      <FixtureListSection
        fixtures={landing}
        now={now}
        droppedCount={fixtures.data?.droppedFixtures?.length ?? 0}
      />

      {hasMore && (
        <p className={styles.seeMore}>
          <Cta to="/fixtures" variant="secondary">
            See more fixtures
          </Cta>
        </p>
      )}
    </Section>
  );
}
